import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, FileText, FileImage, Eye, Trash2 } from 'lucide-react';
import { API_URL } from './constants';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';

export const ExtractionsHistoryTab = ({ onViewResult }) => {
  const { t } = useTranslation('ocr');
  const { t: tCommon } = useTranslation('common');
  const [extractions, setExtractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [extractionToDelete, setExtractionToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchExtractions();
  }, []);

  const fetchExtractions = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/ocr/extractions`);
      setExtractions(response.data);
    } catch (error) {
      console.error('Error fetching extractions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (extraction) => {
    setExtractionToDelete(extraction);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!extractionToDelete) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/ocr/extractions/${extractionToDelete.id}`);
      setExtractions(prev => prev.filter(e => e.id !== extractionToDelete.id));
      toast.success(t('messages.deletedSuccess'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('messages.errorDeleting'));
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setExtractionToDelete(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#E63946]" />
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3">
        {extractions.length > 0 ? (
          extractions.map(ext => (
            <Card 
              key={ext.id} 
              className="hover:shadow-md transition-shadow"
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-lg bg-[#FDE8EA] dark:bg-[#E63946]/20 flex items-center justify-center">
                      <FileImage className="h-6 w-6 text-[#E63946]" />
                    </div>
                    <div>
                      <p className="font-medium">{ext.filename}</p>
                      <p className="text-sm text-muted-foreground">
                        {ext.provider} / {ext.model}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">
                        {new Date(ext.processed_at).toLocaleDateString('es-CO')}
                      </p>
                      <Badge className={ext.error ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'}>
                        {ext.error ? t('history.error') : t('history.success')}
                      </Badge>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => onViewResult({
                        data: ext.extracted_data,
                        provider: ext.provider,
                        model: ext.model,
                        extraction_id: ext.id
                      }, { name: ext.filename })}
                      data-testid={`view-extraction-${ext.id}`}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      {t('history.view')}
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                      onClick={() => handleDeleteClick(ext)}
                      data-testid={`delete-extraction-${ext.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <div className="text-center py-12 text-muted-foreground">
            <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
            <p>{t('history.noExtractions')}</p>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('history.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('history.deleteDescription', { filename: extractionToDelete?.filename })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>{tCommon('cancel')}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteConfirm}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {tCommon('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ExtractionsHistoryTab;
